import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { Calendar } from '@vk-hr-tek/core/calendar';
import { WorkScheduleCalendar } from '@vk-hr-tek/core/calendar/gen/calendar';
import { AppError } from '@vk-hr-tek/core/error';

import { ThunkExtra } from '@app/store';
import {
  GetUserWidgetCalendarResponse,
  GetUserWidgetCalendarParams,
  DashboardService,
} from '@app/gen/dashboard';
import { DynamicEntity } from '@app/types';

import { DashboardState } from '../../dashboard.state';

export const setCompanyId = createAction<{
  companyId: string | undefined;
}>('dashboard/calendar/setCompanyId');

export const setIncludedEmployees = createAction<{
  employees: string[];
}>('dashboard/calendar/setIncludedEmployees');

export const getCalendarData = createAsyncThunk<
  GetUserWidgetCalendarResponse,
  GetUserWidgetCalendarParams,
  ThunkExtra
>(
  'dashboard/calendar/getCalendarData',
  async (getCalendarDto, { rejectWithValue, dispatch, extra: { inject } }) => {
    try {
      const service = inject(DashboardService);

      const result = await service.getUserWidgetCalendar({
        params: getCalendarDto,
      });

      const company = result.companies[0];

      dispatch(setCompanyId({ companyId: company?.id }));
      dispatch(
        setIncludedEmployees({
          employees: company?.employees.map((employee) => employee.id) || [],
        }),
      );

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getWorkSchedule = createAsyncThunk<
  WorkScheduleCalendar | null,
  { year: number; employeeId: string },
  ThunkExtra
>(
  'dashboard/calendar/getWorkSchedule',
  async ({ year, employeeId }, { rejectWithValue, extra: { inject } }) => {
    try {
      const calendar = inject(Calendar);

      return await calendar.downloadWorkSchedule(year, employeeId);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const calendarReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<DynamicEntity, string> & DashboardState
  >,
) => {
  builder.addCase(setCompanyId, (state, { payload }) => {
    state.calendar.companyId = payload.companyId;
    state.calendar.includedEmployees =
      state.calendar.companies
        .find((company) => company.id === payload.companyId)
        ?.employees.map((employee) => employee.id) || [];
  });
  builder.addCase(setIncludedEmployees, (state, { payload }) => {
    state.calendar.includedEmployees = payload.employees;
  });
  builder.addCase(getCalendarData.pending, (state) => {
    state.calendar.status = 'loading';
    state.calendar.companies = [];
  });
  builder.addCase(getCalendarData.fulfilled, (state, { payload }) => {
    state.calendar.status = 'complete';
    state.calendar.companies = payload.companies;
  });
  builder.addCase(getCalendarData.rejected, (state) => {
    state.calendar.status = 'failed';
    state.calendar.companies = [];
  });
  builder.addCase(getWorkSchedule.pending, (state) => {
    state.calendar.workSchedule = null;
  });
  builder.addCase(getWorkSchedule.fulfilled, (state, { payload }) => {
    state.calendar.workSchedule = payload;
  });
  builder.addCase(getWorkSchedule.rejected, (state) => {
    state.calendar.workSchedule = null;
  });
};
